/**
 * WLSN Blog Search Component
 *
 * Wraps wlsn-search-form on the blog listing page and converts it from a
 * full-page redirect into an AJAX search with pagination support.
 *
 * How it works:
 * - Intercepts form submit → prevents redirect, runs AJAX instead
 * - Intercepts form reset → restores original blog listing
 * - Debounced input (400 ms) + immediate submit
 * - AbortController cancels in-flight requests on new input
 * - Fetches /search?type=article&q=QUERY&sections=wlsn-blog-search-results
 *   via Shopify's Section Rendering API — Liquid renders cards server-side
 * - Replaces the entire wlsn-blog-facet innerHTML (grid + pagination together)
 * - Pagination intercept: a CAPTURE-phase listener on wlsn-blog-facet stops
 *   pagination clicks before wlsn-pagination.js fires wlsn:pagination:page-changed,
 *   then refetches the section with the correct search URL + page number
 * - On reset: removes capture listener, restores original facet innerHTML
 * - URL never changes, no page reload
 *
 * HTML structure (wlsn-main-blog-hero.liquid):
 * <wlsn-blog-search>
 *   {% render 'wlsn-search-form', query_type: false, ... %}
 * </wlsn-blog-search>
 */

if (!customElements.get('wlsn-blog-search')) {
  class WLSNBlogSearch extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.debounceTimer = null;
      this.debounceDelay = 400;
      this.isSearchActive = false;

      /** @type {string} Last executed search query — used when paginating search results */
      this.currentQuery = '';

      /** @type {string|null} Cached original wlsn-blog-facet innerHTML for restoration */
      this._originalFacetHTML = null;

      /** @type {Function|null} Capture-phase click handler attached to wlsn-blog-facet */
      this._paginationCaptureHandler = null;

      this.classes = {
        loading: 'loading',
        facet: 'wlsn-blog-facet',
        articlesGrid: 'wlsn-main-blog__articles',
        emptyState: 'wlsn-blog-search__empty',
      };

      this.bindMethods = {
        onInput: this._onInput.bind(this),
        onSubmit: this._onSubmit.bind(this),
        onReset: this._onReset.bind(this),
      };
    }

    /**
     * Form/input scoped to this element; facet looked up on the document.
     * @returns {object}
     */
    get elements() {
      return {
        form: this.querySelector('form'),
        input: this.querySelector('[name="q"]'),
        resetButton: this.querySelector('.js-wlsn-search-show-reset-button'),
        facet: document.querySelector(`.${this.classes.facet}`),
      };
    }

    /**
     * Wires event listeners.
     * @returns {void}
     */
    connectedCallback() {
      const { form, input } = this.elements;

      if (form) {
        form.addEventListener('submit', this.bindMethods.onSubmit);
        form.addEventListener('reset', this.bindMethods.onReset);
      }
      if (input) {
        input.addEventListener('input', this.bindMethods.onInput);
      }

      this.setAttribute('data-wlsn-hydrated', '');
    }

    /**
     * Removes event listeners and aborts any pending request.
     * @returns {void}
     */
    disconnectedCallback() {
      const { form, input } = this.elements;

      if (form) {
        form.removeEventListener('submit', this.bindMethods.onSubmit);
        form.removeEventListener('reset', this.bindMethods.onReset);
      }
      if (input) {
        input.removeEventListener('input', this.bindMethods.onInput);
      }

      this._removePaginationCapture();
      this._abort();
      clearTimeout(this.debounceTimer);
    }

    // ─── Event handlers ────────────────────────────────────────────────────────

    /**
     * Prevents form redirect; runs AJAX search on submit.
     * @param {SubmitEvent} event
     * @returns {void}
     */
    _onSubmit(event) {
      event.preventDefault();
      clearTimeout(this.debounceTimer);
      const { input } = this.elements;
      const query = input ? input.value.trim() : '';
      if (!query) {
        this._reset();
        return;
      }
      this._updateResetButton(true);
      this._search(query);
    }

    /**
     * Fires after the browser clears the input (type="reset" button).
     * @returns {void}
     */
    _onReset() {
      this._reset();
    }

    /**
     * Debounced input handler.
     * @param {InputEvent} event
     * @returns {void}
     */
    _onInput(event) {
      clearTimeout(this.debounceTimer);
      const query = event.target.value.trim();

      if (!query) {
        this._updateResetButton(false);
        this._reset();
        return;
      }

      this._updateResetButton(true);
      this.debounceTimer = setTimeout(() => this._search(query), this.debounceDelay);
    }

    // ─── Core search ───────────────────────────────────────────────────────────

    /**
     * Stores original content, then fetches and renders search results for page 1.
     * @param {string} query
     * @returns {Promise<void>}
     */
    async _search(query) {
      this.currentQuery = query;
      this._storeOriginal();

      const url = `/search?type=article&q=${encodeURIComponent(query)}&sections=wlsn-blog-search-results`;
      await this._fetchSection(url);
    }

    /**
     * Fetches a specific page of the current search query.
     * Called by the pagination capture handler.
     * @param {number|string} page
     * @returns {Promise<void>}
     */
    async _searchPage(page) {
      const url = `/search?type=article&q=${encodeURIComponent(this.currentQuery)}&page=${page}&sections=wlsn-blog-search-results`;
      await this._fetchSection(url);
    }

    /**
     * Shared fetch+render logic used by both initial search and pagination.
     * @param {string} url
     * @returns {Promise<void>}
     */
    async _fetchSection(url) {
      this._abort();
      this._setLoading(true);

      try {
        this.abortController = new AbortController();
        const response = await fetch(url, { signal: this.abortController.signal });

        if (!response.ok) throw new Error(`[WLSNBlogSearch] HTTP ${response.status}`);

        const data = await response.json();
        const sectionHTML = data['wlsn-blog-search-results'];

        if (!sectionHTML) throw new Error('[WLSNBlogSearch] Section missing from response');

        this._renderResults(sectionHTML);
        this.isSearchActive = true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[WLSNBlogSearch] Fetch error:', error);
        }
      } finally {
        this._setLoading(false);
      }
    }

    // ─── DOM manipulation ──────────────────────────────────────────────────────

    /**
     * Caches the entire wlsn-blog-facet innerHTML on first search.
     * Subsequent calls are no-ops.
     * @returns {void}
     */
    _storeOriginal() {
      if (this._originalFacetHTML !== null) return;
      const { facet } = this.elements;
      this._originalFacetHTML = facet ? facet.innerHTML : '';
    }

    /**
     * Parses the section HTML returned by Shopify and replaces the entire
     * wlsn-blog-facet content (article grid + pagination) with search results.
     * Attaches a capture-phase pagination handler afterwards.
     * @param {string} sectionHTML
     * @returns {void}
     */
    _renderResults(sectionHTML) {
      const { facet } = this.elements;
      if (!facet) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');

      // Section Rendering API wraps output in <div id="shopify-section-{name}">
      const sectionWrapper = doc.querySelector('#shopify-section-wlsn-blog-search-results');
      const source = sectionWrapper ?? doc.body;

      facet.innerHTML = source.innerHTML;

      // Attach pagination intercept after new content is in the DOM
      this._attachPaginationCapture(facet);
    }

    /**
     * Restores original blog listing, removes pagination capture listener.
     * @returns {void}
     */
    _reset() {
      this._abort();
      clearTimeout(this.debounceTimer);

      if (!this.isSearchActive && this._originalFacetHTML === null) return;

      const { facet } = this.elements;

      this._removePaginationCapture();

      if (facet && this._originalFacetHTML !== null) {
        // Restoring innerHTML re-triggers connectedCallback on all custom elements
        // inside (wlsn-pagination etc.) so their listeners re-attach automatically.
        facet.innerHTML = this._originalFacetHTML;
      }

      this.isSearchActive = false;
      this.currentQuery = '';
      this._originalFacetHTML = null;
      this._setLoading(false);
      this._updateResetButton(false);
    }

    // ─── Pagination capture ────────────────────────────────────────────────────

    /**
     * Attaches a CAPTURE-phase click listener on wlsn-blog-facet.
     *
     * WHY CAPTURE: wlsn-pagination.js listens in BUBBLE phase. Capture fires first,
     * so calling stopImmediatePropagation() here prevents the click from ever
     * reaching wlsn-pagination — which means it never fires wlsn:pagination:page-changed
     * with the (wrong) blog URL, and wlsn-blog-facet never acts on it.
     * We then manually fetch the correct search URL with the new page number.
     *
     * @param {HTMLElement} facet
     * @returns {void}
     */
    _attachPaginationCapture(facet) {
      this._removePaginationCapture();

      this._paginationCaptureHandler = (event) => {
        const link = event.target.closest('a[data-page]');
        if (!link) return;

        // Stop the click before wlsn-pagination.js receives it
        event.preventDefault();
        event.stopImmediatePropagation();

        this._searchPage(link.dataset.page);
      };

      facet.addEventListener('click', this._paginationCaptureHandler, true /* capture */);
    }

    /**
     * Removes the capture-phase click listener from wlsn-blog-facet.
     * @returns {void}
     */
    _removePaginationCapture() {
      const { facet } = this.elements;
      if (this._paginationCaptureHandler && facet) {
        facet.removeEventListener('click', this._paginationCaptureHandler, true);
        this._paginationCaptureHandler = null;
      }
    }

    // ─── Utilities ─────────────────────────────────────────────────────────────

    /**
     * @param {boolean} hasValue
     * @returns {void}
     */
    _updateResetButton(hasValue) {
      const { resetButton } = this.elements;
      if (!resetButton) return;
      resetButton.classList.toggle('active', hasValue);
    }

    /**
     * @param {boolean} isLoading
     * @returns {void}
     */
    _setLoading(isLoading) {
      const { facet } = this.elements;
      if (!facet) return;
      facet.classList.toggle(this.classes.loading, isLoading);
    }

    /**
     * @returns {void}
     */
    _abort() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }
  }

  customElements.define('wlsn-blog-search', WLSNBlogSearch);
}
